/**
 * Physical Compatibility Engine
 *
 * Handles physical dimension and clearance compatibility checks
 */

import type {
  CompatibilityResult,
  CompatibilityChecker,
  Clearance
} from '../types/CompatibilityResult'
import type { MemoryModel } from '../../models/memory/memory-model'
import type { CoolerModel } from '../../models/cooler/cpu-cooler-model'
import type { GPUModel } from '../../models/gpu/gpu-model'
import type { CaseListItem } from '../../models/case/case-list-model'
import type { CaseDetailModel } from '../../models/case/case-detail-model'
import type { PSUListItem } from '../../models/psu/psu-list-model'

/**
 * Memory-Cooler Physical Interference Checker
 */
export class MemoryCoolerCompatibilityChecker implements CompatibilityChecker<MemoryModel, CoolerModel> {
  check(memory: MemoryModel, cooler: CoolerModel): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    // Height clearance check
    const heightCheck = this.checkHeightClearance(memory, cooler)
    results.push(heightCheck)

    // Position-specific interference check
    const positionCheck = this.checkPositionInterference(memory, cooler)
    if (positionCheck) results.push(positionCheck)

    return results
  }

  getScore(memory: MemoryModel, cooler: CoolerModel): number {
    const clearance = this.calculateClearance(memory, cooler)
    if (clearance.margin < 0) return 0
    if (clearance.margin < 5) return 60
    if (clearance.margin < 10) return 80
    return 100
  }

  private checkHeightClearance(memory: MemoryModel, cooler: CoolerModel): CompatibilityResult {
    const clearance = this.calculateClearance(memory, cooler)

    if (clearance.margin < 0) {
      return {
        level: 'warning',
        category: 'physical',
        message: `メモリとクーラーが物理的に干渉する可能性があります`,
        details: `メモリ高さ: ${memory.physical?.height ?? 'N/A'}mm, クーラークリアランス: ${cooler.compatibility?.ramClearance ?? 'N/A'}mm`,
        solutions: [
          'メモリスロットの位置を変更する',
          'より低いメモリモジュールに交換する',
          '簡易水冷クーラーへの変更を検討する'
        ],
        score: this.getScore(memory, cooler)
      }
    }

    if (clearance.margin < 5) {
      return {
        level: 'warning',
        category: 'physical',
        message: `メモリとクーラーのクリアランスが非常にタイトです`,
        details: `余裕: ${clearance.margin.toFixed(1)}mm`,
        solutions: [
          '組み立て時に注意深く作業する',
          'より余裕のあるメモリまたはクーラーを検討する'
        ],
        score: this.getScore(memory, cooler)
      }
    }

    return {
      level: 'info',
      category: 'physical',
      message: `メモリとクーラーのクリアランスは十分です`,
      details: `余裕: ${clearance.margin.toFixed(1)}mm`,
      score: this.getScore(memory, cooler)
    }
  }

  private checkPositionInterference(memory: MemoryModel, cooler: CoolerModel): CompatibilityResult | null {
    // RGB装飾やヒートスプレッダーの影響をチェック
    // features field removed in Phase 6, physical is optional
    const hasRGB = memory.rgb ?? memory.physical?.rgb ?? false
    const hasLargeHeatspreader = (memory.physical?.height ?? 0) > 40

    if ((hasRGB || hasLargeHeatspreader) && cooler.type === 'Air Tower') {
      return {
        level: 'info',
        category: 'physical',
        message: `装飾付きメモリと大型クーラーの組み合わせです`,
        details: 'RGB装飾やヒートスプレッダーが干渉する可能性があります',
        solutions: [
          'メモリスロットの配置を確認する',
          '必要に応じて別のスロットを使用する'
        ]
      }
    }

    return null
  }

  private calculateClearance(memory: MemoryModel, cooler: CoolerModel): Clearance {
    const memoryHeight = memory.physical?.height ?? 32  // Default 32mm
    const coolerClearance = cooler.compatibility?.ramClearance ?? 40  // Default 40mm
    const margin = coolerClearance - memoryHeight

    return {
      required: memoryHeight,
      available: coolerClearance,
      margin,
      adequate: margin >= 0
    }
  }
}

/**
 * GPU-Case Physical Compatibility Checker
 */
export class GPUCaseCompatibilityChecker implements CompatibilityChecker<GPUModel, CaseListItem> {
  check(gpu: GPUModel, pcCase: CaseListItem): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    // Length compatibility
    const lengthCheck = this.checkLengthCompatibility(gpu, pcCase)
    results.push(lengthCheck)

    // Slot width compatibility
    const widthCheck = this.checkSlotWidthCompatibility(gpu, pcCase)
    if (widthCheck) results.push(widthCheck)

    return results
  }

  getScore(gpu: GPUModel, pcCase: CaseListItem): number {
    // Phase 8: CaseListItem restructured, use maxGPULength directly
    const lengthMargin = pcCase.maxGPULength - gpu.length
    // Phase 5: GPU has slots field directly, Phase 8: Case has expansionSlots optionally
    const caseSlots = pcCase.pciSlots || 7  // Default 7 slots
    const slotsFit = caseSlots >= gpu.slots

    if (!slotsFit) return 0
    if (lengthMargin < 0) return 0
    if (lengthMargin < 20) return 70
    if (lengthMargin < 50) return 85
    return 100
  }

  private checkLengthCompatibility(gpu: GPUModel, pcCase: CaseListItem): CompatibilityResult {
    const gpuLength = gpu.length
    const caseMaxLength = pcCase.maxGPULength
    const margin = caseMaxLength - gpuLength

    if (margin < 0) {
      return {
        level: 'critical',
        category: 'physical',
        message: `GPU がケースに収まりません`,
        details: `GPU長: ${gpuLength}mm, ケース対応: ${caseMaxLength}mm`,
        solutions: [
          'より大きなケースを選択する',
          'より短いGPUを選択する',
          'ケース内部の障害物を確認する'
        ],
        score: 0
      }
    }

    if (margin < 20) {
      return {
        level: 'warning',
        category: 'physical',
        message: `GPU とケースのクリアランスが非常にタイトです`,
        details: `余裕: ${margin}mm`,
        solutions: [
          'ケース内のケーブル配線に注意する',
          'より余裕のあるケースを検討する'
        ],
        score: this.getScore(gpu, pcCase)
      }
    }

    return {
      level: 'info',
      category: 'physical',
      message: `GPU とケースのクリアランスは十分です`,
      details: `余裕: ${margin}mm`,
      score: this.getScore(gpu, pcCase)
    }
  }

  private checkSlotWidthCompatibility(gpu: GPUModel, pcCase: CaseListItem): CompatibilityResult | null {
    const gpuSlots = gpu.slots
    const caseSlots = pcCase.pciSlots || 7  // Default 7 slots

    if (gpuSlots > caseSlots) {
      return {
        level: 'critical',
        category: 'physical',
        message: `GPU のスロット幅がケースの対応数を超えています`,
        details: `GPU必要スロット: ${gpuSlots}, ケース対応: ${caseSlots}`,
        solutions: [
          'より多くのPCIスロットを持つケースを選択する',
          'より薄いGPUを選択する'
        ],
        score: 0
      }
    }

    return null
  }
}

/**
 * Cooler-Case Height Compatibility Checker
 * Checks if CPU cooler fits within case's maximum cooler height clearance
 */
export class CoolerCaseCompatibilityChecker implements CompatibilityChecker<CoolerModel, CaseListItem> {
  check(cooler: CoolerModel, pcCase: CaseListItem): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    // Height clearance check
    const heightCheck = this.checkHeightCompatibility(cooler, pcCase)
    results.push(heightCheck)

    return results
  }

  getScore(cooler: CoolerModel, pcCase: CaseListItem): number {
    const coolerHeight = cooler.height ?? 0
    const caseMaxHeight = pcCase.maxCpuCoolerHeight ?? 0

    // No data available
    if (coolerHeight === 0 || caseMaxHeight === 0) return 100

    const margin = caseMaxHeight - coolerHeight

    if (margin < 0) return 0
    if (margin < 5) return 60
    if (margin < 10) return 80
    return 100
  }

  private checkHeightCompatibility(cooler: CoolerModel, pcCase: CaseListItem): CompatibilityResult {
    const coolerHeight = cooler.height ?? 0
    const caseMaxHeight = pcCase.maxCpuCoolerHeight ?? 0

    // Data missing
    if (coolerHeight === 0 || caseMaxHeight === 0) {
      return {
        level: 'info',
        category: 'physical',
        message: 'クーラーまたはケースの高さデータが不足しています',
        details: 'メーカー仕様を確認してください',
        score: 100
      }
    }

    const margin = caseMaxHeight - coolerHeight

    // Critical: Cooler doesn't fit
    if (margin < 0) {
      return {
        level: 'critical',
        category: 'physical',
        message: 'CPUクーラーがケースに収まりません',
        details: `クーラー高さ: ${coolerHeight}mm, ケース対応: ${caseMaxHeight}mm (${Math.abs(margin)}mm超過)`,
        solutions: [
          'より大きなケースを選択する',
          'より低いクーラーを選択する',
          '簡易水冷クーラーへの変更を検討する'
        ],
        score: 0
      }
    }

    // Warning: Very tight clearance
    if (margin < 5) {
      return {
        level: 'warning',
        category: 'physical',
        message: 'クーラーとケースのクリアランスが非常にタイトです',
        details: `余裕: ${margin}mm`,
        solutions: [
          '組み立て時に注意深く作業する',
          'ケースのサイドパネルが閉まるか確認する',
          'より余裕のあるケースまたはクーラーを検討する'
        ],
        score: this.getScore(cooler, pcCase)
      }
    }

    // Warning: Tight clearance
    if (margin < 10) {
      return {
        level: 'warning',
        category: 'physical',
        message: 'クーラーとケースのクリアランスがタイトです',
        details: `余裕: ${margin}mm`,
        solutions: [
          'ケーブル配線に注意する',
          'より余裕のあるケースを検討する'
        ],
        score: this.getScore(cooler, pcCase)
      }
    }

    // Info: Good clearance
    return {
      level: 'info',
      category: 'physical',
      message: 'クーラーとケースのクリアランスは十分です',
      details: `余裕: ${margin}mm`,
      score: this.getScore(cooler, pcCase)
    }
  }
}

/**
 * PSU-Case Form Factor Compatibility Checker
 * Checks if PSU form factor is supported by the case
 */
export class PSUCaseCompatibilityChecker implements CompatibilityChecker<PSUListItem, CaseListItem> {
  check(psu: PSUListItem, pcCase: CaseListItem): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    // Form factor compatibility check
    const formFactorCheck = this.checkFormFactorCompatibility(psu, pcCase)
    results.push(formFactorCheck)

    return results
  }

  getScore(psu: PSUListItem, pcCase: CaseListItem): number {
    const psuFormFactor = psu.formFactor
    const caseSupportedFormFactors = pcCase.psuSupport

    // Check if PSU form factor is supported
    const isSupported = caseSupportedFormFactors.some(
      supportedFF => supportedFF.toLowerCase() === psuFormFactor.toLowerCase()
    )

    return isSupported ? 100 : 0
  }

  private checkFormFactorCompatibility(psu: PSUListItem, pcCase: CaseListItem): CompatibilityResult {
    const psuFormFactor = psu.formFactor
    const caseSupportedFormFactors = pcCase.psuSupport

    // Check if PSU form factor is supported
    const isSupported = caseSupportedFormFactors.some(
      supportedFF => supportedFF.toLowerCase() === psuFormFactor.toLowerCase()
    )

    if (!isSupported) {
      return {
        level: 'critical',
        category: 'physical',
        message: 'PSUのフォームファクターがケースに対応していません',
        details: `PSU: ${psuFormFactor}, ケース対応: ${caseSupportedFormFactors.join(', ')}`,
        solutions: [
          `${caseSupportedFormFactors.join('/')}フォームファクターのPSUを選択する`,
          `${psuFormFactor}フォームファクター対応のケースを選択する`
        ],
        score: 0
      }
    }

    // Success: Compatible
    return {
      level: 'info',
      category: 'physical',
      message: 'PSUのフォームファクターはケースに対応しています',
      details: `PSU: ${psuFormFactor}, ケース対応: ${caseSupportedFormFactors.join(', ')}`,
      score: 100
    }
  }
}

/**
 * AIO-Case Radiator Compatibility Checker
 * Checks if AIO cooler's radiator size is supported by case
 */
export class AIOCaseCompatibilityChecker implements CompatibilityChecker<CoolerModel, CaseDetailModel> {
  check(cooler: CoolerModel, caseDetail: CaseDetailModel): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    // Skip if not AIO cooler
    if (!cooler.radiatorSize) {
      return results
    }

    const radiatorCheck = this.checkRadiatorCompatibility(cooler, caseDetail)
    results.push(radiatorCheck)

    return results
  }

  getScore(cooler: CoolerModel, caseDetail: CaseDetailModel): number {
    // Not AIO cooler
    if (!cooler.radiatorSize) return 100

    // No radiator mount data
    if (!caseDetail.cooling?.radiatorMounts || caseDetail.cooling.radiatorMounts.length === 0) {
      return 100  // Unknown, assume compatible
    }

    // Check if any mount supports this radiator size
    const compatibleMounts = caseDetail.cooling.radiatorMounts.filter(
      mount => mount.maxSize >= cooler.radiatorSize!
    )

    return compatibleMounts.length > 0 ? 100 : 0
  }

  private checkRadiatorCompatibility(
    cooler: CoolerModel,
    caseDetail: CaseDetailModel
  ): CompatibilityResult {
    const radiatorSize = cooler.radiatorSize!
    const radiatorMounts = caseDetail.cooling?.radiatorMounts ?? []

    // No radiator mount data available
    if (radiatorMounts.length === 0) {
      return {
        level: 'warning',
        category: 'physical',
        message: 'ケースのラジエーター対応情報がありません',
        details: 'メーカー仕様を確認してください',
        score: 100
      }
    }

    // Find compatible mounts
    const compatibleMounts = radiatorMounts.filter(
      mount => mount.maxSize >= radiatorSize
    )

    if (compatibleMounts.length === 0) {
      const maxSupported = Math.max(...radiatorMounts.map(m => m.maxSize))
      const availableSizes = [...new Set(radiatorMounts.map(m => m.maxSize))].sort((a, b) => a - b)

      return {
        level: 'critical',
        category: 'physical',
        message: `AIOクーラーのラジエーター (${radiatorSize}mm) がケースに対応していません`,
        details: `ケース対応: 最大${maxSupported}mmまで`,
        solutions: [
          'より大きなケースを選択する',
          `より小さなラジエーターのAIOクーラーを選択する（${maxSupported}mm以下）`,
          `対応サイズ: ${availableSizes.join('mm, ')}mm`
        ],
        score: 0
      }
    }

    // Success: Compatible
    const locations = compatibleMounts.map(m => m.location).join(', ')
    return {
      level: 'info',
      category: 'physical',
      message: `ラジエーター ${radiatorSize}mm がケースに対応しています`,
      details: `マウント可能位置: ${locations} (${compatibleMounts.length}箇所)`,
      score: 100
    }
  }

  /**
   * Phase 4: Check detailed compatibility for each mount location
   * Considers GPU clearance for Front mount and RAM clearance for Top mount
   */
  checkDetailedCompatibility(
    cooler: CoolerModel,
    caseDetail: CaseDetailModel,
    otherComponents: {
      gpu?: GPUModel
      memory?: MemoryModel
    }
  ): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    if (!cooler.radiatorSize) return results

    const radiatorMounts = caseDetail.cooling?.radiatorMounts ?? []
    const compatibleMounts = radiatorMounts.filter(
      mount => mount.maxSize >= cooler.radiatorSize!
    )

    // For each compatible mount location, check constraints
    for (const mount of compatibleMounts) {
      // Front mount: Check GPU clearance
      if (mount.location === 'Front' && otherComponents.gpu) {
        const frontCheck = this.checkFrontMountGPUClearance(
          cooler,
          caseDetail,
          otherComponents.gpu
        )
        if (frontCheck) results.push(frontCheck)
      }

      // Top mount: Check RAM clearance
      if (mount.location === 'Top' && otherComponents.memory) {
        const topCheck = this.checkTopMountRAMClearance(
          cooler,
          caseDetail,
          otherComponents.memory
        )
        if (topCheck) results.push(topCheck)
      }
    }

    return results
  }

  private checkFrontMountGPUClearance(
    cooler: CoolerModel,
    caseDetail: CaseDetailModel,
    gpu: GPUModel
  ): CompatibilityResult | null {
    // Estimate radiator + fan thickness (average 55mm total)
    // Radiator ~27-30mm + Fan 25mm = 52-55mm
    const radiatorThickness = 27  // Average radiator thickness
    const fanThickness = 25       // Standard 25mm fan
    const totalThickness = radiatorThickness + fanThickness

    // Calculate effective GPU length
    const caseMaxGPULength = caseDetail.maxGPULength ?? 0
    const effectiveGPULength = caseMaxGPULength - totalThickness

    if (gpu.length > effectiveGPULength) {
      return {
        level: 'warning',
        category: 'physical',
        message: 'Front装着時、GPUが入らない可能性があります',
        details: `GPU長: ${gpu.length}mm, 装着可能: 約${effectiveGPULength}mm (ラジエーター厚み考慮)`,
        solutions: [
          'Top または Rear にラジエーターを装着する',
          'より短いGPUを選択する',
          'より大きなケースを選択する'
        ],
        score: 70
      }
    }

    // Tight clearance warning (less than 20mm margin)
    const margin = effectiveGPULength - gpu.length
    if (margin < 20) {
      return {
        level: 'warning',
        category: 'physical',
        message: 'Front装着時、GPUとラジエーターのクリアランスがタイトです',
        details: `余裕: 約${margin}mm`,
        solutions: [
          'ケーブル配線に注意する',
          'Top装着を検討する'
        ],
        score: 85
      }
    }

    return null  // No issues
  }

  private checkTopMountRAMClearance(
    cooler: CoolerModel,
    caseDetail: CaseDetailModel,
    memory: MemoryModel
  ): CompatibilityResult | null {
    // Estimate clearance from top mount to RAM (case-specific, use average)
    // Typical ATX case: 50-60mm clearance
    const topMountToRAM = 50  // mm, conservative estimate
    const memoryHeight = memory.physical?.height ?? 32

    if (memoryHeight > topMountToRAM) {
      return {
        level: 'warning',
        category: 'physical',
        message: 'Top装着時、RAMと干渉する可能性があります',
        details: `RAM高さ: ${memoryHeight}mm, クリアランス: 約${topMountToRAM}mm`,
        solutions: [
          'Front または Rear にラジエーターを装着する',
          'より低いRAMモジュールを選択する',
          'メーカー仕様で確認する'
        ],
        score: 70
      }
    }

    // Info for tall RAM (>40mm) but within clearance
    if (memoryHeight > 40) {
      return {
        level: 'info',
        category: 'physical',
        message: 'Top装着時は高さのあるRAMとの干渉に注意',
        details: `RAM高さ: ${memoryHeight}mm（標準的なRAMは32-35mm）`,
        score: 90
      }
    }

    return null  // No issues
  }
}

/**
 * Physical Compatibility Engine
 * Coordinates all physical compatibility checks
 */
export class PhysicalCompatibilityEngine {
  private memoryCoolerChecker = new MemoryCoolerCompatibilityChecker()
  private gpuCaseChecker = new GPUCaseCompatibilityChecker()
  private coolerCaseChecker = new CoolerCaseCompatibilityChecker()
  private psuCaseChecker = new PSUCaseCompatibilityChecker()
  private aioCaseChecker = new AIOCaseCompatibilityChecker()

  checkMemoryCoolerCompatibility(memory: MemoryModel, cooler: CoolerModel): CompatibilityResult[] {
    return this.memoryCoolerChecker.check(memory, cooler)
  }

  checkGPUCaseCompatibility(gpu: GPUModel, pcCase: CaseListItem): CompatibilityResult[] {
    return this.gpuCaseChecker.check(gpu, pcCase)
  }

  checkCoolerCaseCompatibility(cooler: CoolerModel, pcCase: CaseListItem): CompatibilityResult[] {
    return this.coolerCaseChecker.check(cooler, pcCase)
  }

  checkPSUCaseCompatibility(psu: PSUListItem, pcCase: CaseListItem): CompatibilityResult[] {
    return this.psuCaseChecker.check(psu, pcCase)
  }

  checkAIOCaseCompatibility(cooler: CoolerModel, caseDetail: CaseDetailModel): CompatibilityResult[] {
    return this.aioCaseChecker.check(cooler, caseDetail)
  }

  checkAIODetailedCompatibility(
    cooler: CoolerModel,
    caseDetail: CaseDetailModel,
    otherComponents: {
      gpu?: GPUModel
      memory?: MemoryModel
    }
  ): CompatibilityResult[] {
    return this.aioCaseChecker.checkDetailedCompatibility(cooler, caseDetail, otherComponents)
  }

  getOverallPhysicalScore(components: {
    memory?: MemoryModel
    cooler?: CoolerModel
    gpu?: GPUModel
    case?: CaseListItem
    caseDetail?: CaseDetailModel
    psu?: PSUListItem
  }): number {
    const scores: number[] = []

    if (components.memory && components.cooler) {
      scores.push(this.memoryCoolerChecker.getScore(components.memory, components.cooler))
    }

    if (components.gpu && components.case) {
      scores.push(this.gpuCaseChecker.getScore(components.gpu, components.case))
    }

    if (components.cooler && components.case) {
      scores.push(this.coolerCaseChecker.getScore(components.cooler, components.case))
    }

    if (components.psu && components.case) {
      scores.push(this.psuCaseChecker.getScore(components.psu, components.case))
    }

    // AIO-Case radiator check (Phase 3)
    if (components.cooler && components.caseDetail) {
      scores.push(this.aioCaseChecker.getScore(components.cooler, components.caseDetail))
    }

    return scores.length > 0 ? scores.reduce((a, b) => a + b) / scores.length : 100
  }
}